import React, { Fragment, useEffect, useState } from 'react';
import { useAppActions, useAppState } from '../../../overmind';
import { OrganizationLevelType } from '../../../enums';
import { extractHighestOrganizationLevel } from '../../../library/helpers/permissions/extract-highest-organization-level';
import { Mode } from '../../../navigation/navigation.enums';
import { IPermissionsEntityDropdownWidgetProps, PermissionsEntity } from './permissions-entity-dropdown.models';
import { DropdownSelect, DropdownSelectOption } from '@keplerco/core';

export function PermissionsEntityDropdownWidget(props: IPermissionsEntityDropdownWidgetProps): JSX.Element {
  const actions = useAppActions();
  const { mode, user, permissions, companyVariables } = useAppState();

  const [options, setOptions] = useState<DropdownSelectOption[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<string>('');

  async function generatePermissionsEntities(companySlug: string | undefined): Promise<PermissionsEntity[] | undefined> {
    if (!companySlug) return undefined;
    const result = await actions.getAllDepartments(companySlug);
    return result?.departments?.map(department => ({
      organizationLevel: OrganizationLevelType.Department,
      entity: department,
    }));
  }

  function generateMenuOptions(permissionsEntities: PermissionsEntity[], organizationLevel: OrganizationLevelType, slug: string | undefined) {
    let options: DropdownSelectOption[] = [];

    const allEntity: PermissionsEntity = {
      organizationLevel: user?.isSystemAdmin || organizationLevel === OrganizationLevelType.Company ? OrganizationLevelType.Company : organizationLevel,
      entity: { name: 'All', slug },
    };

    const allOption: DropdownSelectOption = {
      value: 'All',
      onClick: () => handleEntitySelect(allEntity),
    };

    options.push(allOption);

    options = options.concat(
      permissionsEntities.map(permissionsEntity => ({
        value: permissionsEntity.entity.name,
        onClick: () => handleEntitySelect(permissionsEntity),
        selected: selectedEntity === permissionsEntity.entity.name,
      }))
    );

    setOptions(options);
    if (!selectedEntity && !!options[0].onClick) options[0].onClick();
  }

  function handleEntitySelect(entity: PermissionsEntity) {
    setSelectedEntity(entity.entity.name);
    props.onClick(entity);
  }

  useEffect(() => {
    async function getData() {
      // role based
      if (mode === Mode.AdminDashboard) {
        const entities = await generatePermissionsEntities(companyVariables.slug);
        if (!entities) return;

        generateMenuOptions(entities, OrganizationLevelType.Department, companyVariables.slug);
        return;
      }

      // permissions based
      if (!permissions) return;
      const organizationLevel = extractHighestOrganizationLevel(permissions?.analytics.organizationLevels);
      if (!organizationLevel) return;

      // company
      if (organizationLevel.organizationLevel === OrganizationLevelType.Company) {
        const entities = await generatePermissionsEntities(organizationLevel.entities[0].slug);
        if (!entities) return;

        generateMenuOptions(entities, OrganizationLevelType.Company, organizationLevel?.entities[0].slug);
        return;
      }

      // department
      if (organizationLevel.organizationLevel === OrganizationLevelType.Department) {
        generateMenuOptions(
          organizationLevel.entities.map(entity => ({
            organizationLevel: OrganizationLevelType.Department,
            entity,
          })),
          OrganizationLevelType.Department,
          undefined
        );
        return;
      }

      // team
      if (organizationLevel.organizationLevel === OrganizationLevelType.Team) {
        generateMenuOptions(
          organizationLevel.entities.map(entity => ({
            organizationLevel: OrganizationLevelType.Team,
            entity,
          })),
          OrganizationLevelType.Team,
          undefined
        );
        return;
      }
    }

    getData();
  }, [mode, companyVariables.slug, permissions]);

  return <Fragment>{options.length > 0 && <DropdownSelect label="Filter By" value={selectedEntity} options={options} />}</Fragment>;
}
